import { registerPlugin } from '@capacitor/core';

// Plugin nativo in-project (não publicado em npm).
// Implementação:
//   - ios/App/App/VxlBatchDownloader/VxlBatchDownloader.swift
//   - android/app/src/main/java/br/com/pagtrust/ead/plugins/vxlbatchdownloader/*.java
//
// API (todos os métodos retornam Promise):
//
//   enqueue({ batchId, destDir, concurrency, items, encryption?, enrollId?, network? })
//     - items: [{ id, url, filename, headers?, segmentIndex? }]
//     - destDir: relativo a Directory.Data (sandbox interno do app)
//     - network (opcional): 'wifi-only' bloqueia cada download em cellular —
//         iOS via URLRequest.allowsCellularAccess=false, Android via
//         ConnectivityManager antes de cada item. Se a rede virar cellular
//         mid-batch, os itens restantes falham com "wifi-only: network ..."
//         (tratado como pausa de rede no JS). Ausente = sem restrição.
//     - encryption (opcional): { algorithm: 'aes-128-cbc', keyAlias: string }
//         Quando presente, cada item com segmentIndex >= 0 é cifrado on-write
//         usando uma File Key hardware-backed identificada por keyAlias (tipic.
//         o loId). Items sem segmentIndex são gravados em plaintext mesmo no
//         batch cifrado (caso raro nesse fluxo).
//         - aes-128-cbc (HLS): + PKCS7, IV implícito = uint128_be(segmentIndex)
//           — formato exigido pelo VHS quando lê #EXT-X-KEY:METHOD=AES-128.
//     - enrollId (opcional): a qual enrollment esse batch pertence. Quando
//         presente JUNTO com encryption, registra associação loId↔enrollId no
//         índice nativo (LoEnrollIndex) — usado pelo scheme handler vxlkey://
//         pra carregar o lease correto antes de devolver a chave AES no playback.
//         Sem enrollId, o lease nunca é validado e a chave é bloqueada por
//         padrão (defesa em profundidade contra downloads sem associação).
//     - resolve assim que persistido o estado e disparado o download; NÃO espera completar
//
//   getStatus({ batchId }) → { state, done, total, failedItems, bytesDownloaded }
//     - state: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'unknown'
//
//   cancel({ batchId })
//
//   cleanupBatch({ batchId })
//     - remove o JSON de estado persistido (NÃO remove os arquivos baixados nem a File Key)
//
//   removeKey({ keyAlias })
//     - apaga a File Key cifrada do KeyVault (hardware-backed) identificada por keyAlias.
//     - chamado quando o user remove uma aula offline. Idempotente.
//
//   setLoEnrollMap({ map }) -> { changed }
//     - reconstrói em lote as associações loId->enrollId do índice nativo, fazendo
//       MERGE com o que já existe. As chaves do map devem estar em safeLoId(),
//       a mesma forma usada no keyAlias e na URI vxlkey://<safeId>.
//     - existe porque sem índice o gate de chave nega tudo (fail-closed) e a aula
//       baixada não toca nem online. O JS é dono autoritativo do mapeamento.
//
//   nukeAll()
//     - cancela TODOS os batches ativos, apaga estados persistidos em _batchdl_state/
//       e TODAS as File Keys. Não toca em arquivos baixados (JS purge via Filesystem).
//     - usado na migração de schema de cifragem ao detectar versão antiga do cache.
//     - idempotente; seguro chamar mesmo sem batches ativos.
//
//   listActiveBatches() → { batchIds: string[] }
//
//   replaceItem({ batchId, itemId, url, headers? })
//     - reenfileira item específico com nova URL (caso o token tenha expirado e o link assinado fique 403)
//
//   getFileInfo({ path }) → { exists, size, type }
//     - path relativo a Directory.Data (mesma raiz que destDir).
//     - size em bytes (0 quando não existe ou é diretório); type: 'file' |
//       'directory' | null. Usado pra verificar integridade de downloads
//       (tamanho no disco vs esperado) sem ler o arquivo pelo bridge.
//
//   getDirectorySize({ path }) → { bytes }
//     - path relativo a Directory.Data (mesma raiz que destDir). Soma NATIVA dos
//       bytes dos arquivos do diretório (não-recursivo) num único número.
//     - substitui Filesystem.readdir+reduce no computeOfflineSize: o readdir
//       marshalava centenas de entradas (size/mtime/ctime/uri por segmento HLS)
//       pelo bridge, travando a finalização do download por segundos. Aqui só
//       trafega 1 número.
//
//   getFreeDiskSize() → { bytes }
//     - espaço livre em bytes na partição onde os arquivos serão escritos (sandbox interno).
//     - iOS: volumeAvailableCapacityForImportantUsageKey (recomendado pela Apple — desconta cache purgable do sistema).
//     - Android: StatFs(filesDir).availableBytes.
//     - usado pelo hlsNativeDownloader pra abortar antes do enqueue quando não couber.
//
//   setLease({ enrollId, claim })
//     - persiste o lease (carteirinha de validade) assinado HMAC no arquivo
//       nativo {Documents|filesDir}/_batchdl_state/leases/<enrollId>.bin. A
//       chave HMAC é hardware-backed (Keychain iOS / AndroidKeyStore — StrongBox
//       quando disponível). O claim é serializado em JSON canônico (chaves
//       ordenadas) antes de assinar.
//     - claim mínimo esperado pelo scheme handler: { enrollId, studentId,
//       issuedAt, validUntil, graceUntil, revokedAt }.
//
//   getLease({ enrollId }) → { claim, valid, exists }
//     - retorna o lease parseado e flag de integridade do HMAC.
//     - exists=false: nenhum arquivo no disco (download pré-feature ou nunca houve).
//     - valid=false (com exists=true): HMAC não bate — arquivo adulterado ou
//       chave HMAC perdida (re-instalação).
//
//   removeLease({ enrollId })
//     - apaga o arquivo de lease. Idempotente. Chamado no removeAllFromEnroll.
//
//   beginSession() / endSession()
//     - delimitam uma FILA de downloads (N batches em sequência). No Android,
//       com a sessão aberta o ForegroundService segura o foreground ENTRE os
//       batches — sem isso o FGS caía 5s após cada aula e, com o app em
//       background, o OS congelava o processo no gap em que o JS orquestra a
//       próxima (a fila morria sem erro). endSession devolve a parada à
//       carência curta normal; se o JS morrer sem chamar, o service tem um
//       fallback de 5min. iOS: no-op (URLSession.background não precisa).
//
//   setActiveStudent({ studentId })
//     - registra o studentId do user logado em RAM nativa (perdido em kill).
//     - lido pelo scheme handler vxlkey:// pra confirmar que o studentId do
//       lease bate com quem está logado agora — impede acesso da conta A aos
//       downloads da conta B no mesmo device.
//     - passar { studentId: null } ou string vazia zera (usado em logout).
//
// Eventos:
//   'batchProgress'  → { batchId, done, total, percent, bytesDownloaded? }   (debounce ~500ms)
//   'batchCompleted' → { batchId }
//   'batchFailed'    → { batchId, error, failedItems }
//   'itemFailed'     → { batchId, itemId, error, httpStatus? }

export const VxlBatchDownloader = registerPlugin('VxlBatchDownloader', {
  // sem fallback web; usado somente em Capacitor nativo
});

export default VxlBatchDownloader;
